/**
 * CSV Helper Utilities
 * Parse and generate CSV files for email import/export.
 */

/**
 * Parse a CSV string into an array of { email, name } objects.
 * Expects header row with at least an "email" column. "name" column is optional.
 * Handles quoted fields (commas inside quotes) and trims whitespace.
 *
 * @param {string} csvText - Raw CSV content
 * @returns {{ data: Array<{email: string, name: string}>, errors: string[] }}
 */
export function parseEmailCsv(csvText) {
  const errors = [];
  const lines = csvText.split(/\r?\n/).filter(line => line.trim());

  if (lines.length < 2) {
    return { data: [], errors: ['CSV must have a header row and at least one data row'] };
  }

  // Simple CSV field splitter that respects quoted fields
  const splitCsvRow = (row) => {
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let c = 0; c < row.length; c++) {
      const ch = row[c];
      if (ch === '"') { inQuotes = !inQuotes; continue; }
      if (ch === ',' && !inQuotes) { fields.push(current.trim()); current = ''; continue; }
      current += ch;
    }
    fields.push(current.trim());
    return fields;
  };

  // Validate header
  const header = splitCsvRow(lines[0]).map(h => h.toLowerCase());
  const emailIdx = header.indexOf('email');
  const nameIdx = header.indexOf('name');

  if (emailIdx === -1) {
    return { data: [], errors: ['CSV must have an "email" column in the header'] };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const data = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvRow(lines[i]);
    const email = (cols[emailIdx] || '').trim().toLowerCase();
    const name = nameIdx !== -1 ? (cols[nameIdx] || '').trim() : '';

    if (!email) {
      errors.push(`Row ${i + 1}: empty email — skipped`);
      continue;
    }
    if (!emailRegex.test(email)) {
      errors.push(`Row ${i + 1}: invalid email "${email}" — skipped`);
      continue;
    }

    data.push({ email, name });
  }

  return { data, errors };
}

/**
 * Generate a CSV string from an array of email objects.
 *
 * @param {Array<{email: string, name: string, verificationStatus: string, dateAdded: string}>} emails
 * @returns {string} CSV content
 */
export function generateEmailCsv(emails) {
  const header = 'email,name,status,date_added';
  const rows = emails.map(e => {
    const name = (e.name || '').includes(',') ? `"${e.name}"` : (e.name || '');
    // nosemgrep: tui-template-literal-pii-js — email is intentional CSV output, not logged/exposed
    const email = (e.email || '').includes(',') ? `"${e.email}"` : (e.email || '');
    return `${email},${name},${e.verificationStatus || ''},${e.dateAdded || ''}`;
  });
  return [header, ...rows].join('\n');
}

/**
 * Trigger a browser download of a CSV string.
 *
 * @param {string} csvContent - CSV text
 * @param {string} filename - Download filename
 */
export function downloadCsv(csvContent, filename = 'emails.csv') {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
